import { 
  TrendingDown, 
  TrendingUp, 
  Minus, 
  Package, 
  DollarSign, 
  BarChart3,
  Target
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

// VarianceCell component - defined outside main component to avoid re-creation
const VarianceCell = ({ variance }) => {
  if (!variance || variance.isZero) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <Minus className="h-4 w-4" />
        <span>No change</span>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 ${
      variance.isFavorable ? 'text-green-700' : 'text-red-700'
    }`}>
      {variance.isFavorable ? (
        <TrendingUp className="h-4 w-4" />
      ) : (
        <TrendingDown className="h-4 w-4" />
      )}
      <span className="font-medium">
        {variance.sign}{variance.value}
      </span>
      <span className="text-sm">({variance.percent})</span>
    </span>
  );
};

/**
 * SOPOverviewTab - S&OP Overview with generic, high-level strategic insights
 * 
 * Key Metrics (S&OP focused, NOT tariff-focused):
 * - Volume: Total Units, Annual Volume, Volume Growth Rate
 * - Pricing: Avg Unit Price, Price Variance, Base Price
 * - Revenue: Total Revenue, Annual Revenue, Revenue per Unit
 * - Cost: Total Cost, Cost per Unit, Cost Breakdown by Component
 * - Margin: Gross Margin %, Contribution Margin, Margin Variance
 * - Timeline: Life of Program, SOP/EOP Dates, Forecast Period
 */
export default function SOPOverviewTab({ 
  comparisonData, 
  baselineScenario, 
  comparisonScenario,
  scenariosData
}) {
  if (!comparisonData && (!scenariosData || scenariosData.length < 2)) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">No comparison data available. Please select two scenarios to compare.</p>
        </CardContent>
      </Card>
    );
  }

  // Get metrics from scenarios data or comparison data
  const baseline = scenariosData?.find(s => s.scenario_id === baselineScenario?.id) || scenariosData?.[0];
  const comparison = scenariosData?.find(s => s.scenario_id === comparisonScenario?.id) || scenariosData?.[1];

  if (!baseline || !comparison) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">Unable to load scenario metrics. Please select scenarios to compare.</p>
        </CardContent>
      </Card>
    );
  }

  const baselineMetrics = baseline.metrics || baseline;
  const comparisonMetrics = comparison.metrics || comparison;

  // Calculate variances
  const calcVariance = (baseline, comparison) => {
    if (!baseline || !comparison) return { value: 0, percent: 0 };
    const value = comparison - baseline;
    const percent = baseline !== 0 ? (value / baseline) * 100 : 0;
    return { value, percent };
  };

  // Format utilities
  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) return '$0';
    const value = Math.abs(cents) / 100;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US');
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.0%';
    return `${Math.abs(value).toFixed(1)}%`;
  };

  const formatVariance = (variance, isCurrency = false, inverse = false) => {
    const value = variance.value;
    const percent = variance.percent;
    
    // For costs: negative is good (green), positive is bad (red)
    // inverse=true: positive is good (green), negative is bad (red) - for revenue/volume
    const isFavorable = inverse ? value > 0 : value < 0;
    
    return {
      value: isCurrency ? formatCurrency(Math.abs(value)) : formatNumber(Math.abs(value)),
      percent: formatPercent(percent),
      isFavorable,
      isZero: value === 0,
      sign: value > 0 ? '+' : value < 0 ? '-' : '',
    };
  };

  // Calculate all metrics
  const volumeVariance = calcVariance(baselineMetrics.total_volume, comparisonMetrics.total_volume);
  const priceVariance = calcVariance(baselineMetrics.avg_price_per_unit_cents, comparisonMetrics.avg_price_per_unit_cents);
  const revenueVariance = calcVariance(baselineMetrics.total_revenue_cents, comparisonMetrics.total_revenue_cents);
  const costVariance = calcVariance(baselineMetrics.total_tariff_cost_cents, comparisonMetrics.total_tariff_cost_cents);

  // KPI Cards data for the top section
  const kpiCards = [
    {
      label: 'Volume Impact',
      icon: Package,
      baseline: formatNumber(baselineMetrics.total_volume),
      comparison: formatNumber(comparisonMetrics.total_volume),
      variance: formatVariance(volumeVariance, false, true),
      unit: 'units',
    },
    {
      label: 'Price Impact',
      icon: DollarSign,
      baseline: formatCurrency(baselineMetrics.avg_price_per_unit_cents),
      comparison: formatCurrency(comparisonMetrics.avg_price_per_unit_cents),
      variance: formatVariance(priceVariance, true, true),
      unit: '/unit',
    },
    {
      label: 'Revenue Impact',
      icon: BarChart3,
      baseline: formatCurrency(baselineMetrics.total_revenue_cents),
      comparison: formatCurrency(comparisonMetrics.total_revenue_cents),
      variance: formatVariance(revenueVariance, true, true),
      unit: '',
    },
  ];

  // Detailed metrics table data
  const detailedMetrics = [
    {
      label: 'Total Volume',
      baseline: formatNumber(baselineMetrics.total_volume),
      comparison: formatNumber(comparisonMetrics.total_volume),
      variance: formatVariance(volumeVariance, false, true),
    },
    {
      label: 'Avg Unit Price',
      baseline: formatCurrency(baselineMetrics.avg_price_per_unit_cents),
      comparison: formatCurrency(comparisonMetrics.avg_price_per_unit_cents),
      variance: formatVariance(priceVariance, true, true),
    },
    {
      label: 'Total Revenue',
      baseline: formatCurrency(baselineMetrics.total_revenue_cents),
      comparison: formatCurrency(comparisonMetrics.total_revenue_cents),
      variance: formatVariance(revenueVariance, true, true),
    },
    {
      label: 'Total Cost',
      baseline: formatCurrency(baselineMetrics.total_tariff_cost_cents),
      comparison: formatCurrency(comparisonMetrics.total_tariff_cost_cents),
      variance: formatVariance(costVariance, true, false), // inverse=false for costs
    },
    {
      label: 'Forecast Period',
      baseline: `${baselineMetrics.forecast_years || 0} years`,
      comparison: `${comparisonMetrics.forecast_years || 0} years`,
      variance: null,
    },
    {
      label: 'Monthly Forecasts',
      baseline: `${baselineMetrics.forecast_months || 0} months`,
      comparison: `${comparisonMetrics.forecast_months || 0} months`,
      variance: null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          const variance = kpi.variance;
          
          return (
            <Card key={index} className={`border-2 ${
              variance.isZero ? 'border-gray-200' :
              variance.isFavorable ? 'border-green-200' : 'border-red-200'
            }`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-lg ${
                    variance.isZero ? 'bg-gray-100' :
                    variance.isFavorable ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      variance.isZero ? 'text-gray-600' :
                      variance.isFavorable ? 'text-green-600' : 'text-red-600'
                    }`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                    {kpi.label}
                  </span>
                </div>
                
                {/* Variance Percentage - Large Display */}
                <div className={`text-3xl font-bold mb-2 ${
                  variance.isZero ? 'text-gray-600' :
                  variance.isFavorable ? 'text-green-700' : 'text-red-700'
                }`}>
                  {variance.isZero ? '-' : `${variance.sign}${variance.percent}`}
                </div>
                
                {/* Plan vs Comparison Values */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Base:</span>
                    <span className="font-medium text-gray-700">{kpi.baseline}{kpi.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Comp:</span>
                    <span className="font-medium text-gray-700">{kpi.comparison}{kpi.unit}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-1 mt-1 flex justify-between">
                    <span className="text-gray-500">Var:</span>
                    <span className={`font-medium ${
                      variance.isZero ? 'text-gray-600' :
                      variance.isFavorable ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {variance.sign}{variance.value}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Metrics Table */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Detailed Metrics Comparison
          </CardTitle>
          <CardDescription>
            Full breakdown of S&OP metrics between scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-y border-gray-200">
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Metric
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {baselineScenario?.name || baseline.scenario_name || 'Baseline'}
                    <Badge variant="outline" className="ml-2 text-xs">Base</Badge>
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {comparisonScenario?.name || comparison.scenario_name || 'Comparison'}
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Variance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detailedMetrics.map((metric, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {metric.label}
                    </td>
                    <td className="text-right px-6 py-4 font-mono text-gray-700">
                      {metric.baseline}
                    </td>
                    <td className="text-right px-6 py-4 font-mono text-gray-700">
                      {metric.comparison}
                    </td>
                    <td className="text-right px-6 py-4">
                      {metric.variance ? (
                        <VarianceCell variance={metric.variance} />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
